<template>
    <div class="finalize-build-container">
        <div class="finalize-build-card">
            <h3 class="finalize-title">Build Progress</h3>
            <div class="finalize-progress-row">
                <div class="finalize-progress-label">Overall Progress</div>
                <div class="finalize-progress-bar">
                    <div
                        class="finalize-progress-bar-inner"
                        :style="{ width: (progress || 5) + '%' }"
                    ></div>
                    <span class="finalize-progress-percent"
                        >{{ progress || 5 }}%</span
                    >
                </div>
            </div>
            <div class="finalize-steps-list">
                <div
                    v-for="(phase, idx) in displayPhases"
                    :key="phase.phaseType || idx"
                    class="finalize-step-row"
                >
                    <div class="finalize-step-icon-col">
                        <span
                            :class="[
                                'finalize-step-icon',
                                getPhaseStatusClass(phase),
                            ]"
                        >
                            <template v-if="phase.phaseType === 'COMPLETED'">
                                <i class="fas fa-check-circle"></i>
                            </template>
                            <template v-else-if="phase.status === 'SUCCEEDED'">
                                <i class="fas fa-check-circle"></i>
                            </template>
                            <template v-else-if="!phase.status">
                                <i class="fas fa-spinner fa-spin"></i>
                            </template>
                            <template
                                v-else-if="
                                    phase.status === 'FAILED' ||
                                    phase.status === 'CLIENT_ERROR'
                                "
                            >
                                <i class="fas fa-times-circle"></i>
                            </template>
                            <template v-else>
                                <i class="fas fa-circle"></i>
                            </template>
                        </span>
                        <div
                            v-if="
                                idx < displayPhases.length - 1 &&
                                !(
                                    phase.status === 'FAILED' ||
                                    phase.status === 'CLIENT_ERROR'
                                )
                            "
                            class="finalize-step-connector"
                            :class="
                                (displayPhases[idx + 1] &&
                                    displayPhases[idx + 1].status ===
                                        'FAILED') ||
                                displayPhases[idx + 1].status === 'CLIENT_ERROR'
                                    ? 'failed-connector'
                                    : ''
                            "
                        ></div>
                    </div>
                    <div class="finalize-step-content">
                        <div
                            class="finalize-step-title"
                            :class="
                                phase.status === 'FAILED' ||
                                phase.status === 'CLIENT_ERROR'
                                    ? 'failed-title'
                                    : ''
                            "
                        >
                            {{ getPhaseTitle(phase) }}
                        </div>

                        <div
                            class="finalize-step-desc"
                            :class="
                                phase.status === 'FAILED' ||
                                phase.status === 'CLIENT_ERROR'
                                    ? 'failed-desc'
                                    : ''
                            "
                        >
                            {{ getPhaseDesc(phase) }}
                        </div>
                    </div>
                </div>
            </div>
            <slot name="footer-button" :data="[]"></slot>
        </div>
    </div>
</template>

<script>
export default {
    name: "FinalizeBuild",
    props: {
        phases: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            progress: 0,
            buildStatus: null,
            staticPhaseTypes: [
                "SUBMITTED",
                "QUEUED",
                "PROVISIONING",
                "DOWNLOAD_SOURCE",
                "INSTALL",
                "PRE_BUILD",
                "BUILD",
                "POST_BUILD",
                "UPLOAD_ARTIFACTS",
                "FINALIZING",
                "COMPLETED",
            ],
            phaseDetails: {
                SUBMITTED: {
                    title: "Build Request Submitted",
                    desc: "We’ve received your request and started the process.",
                },
                QUEUED: {
                    title: "Waiting in Build Queue",
                    desc: "Your build is in line and will start soon.",
                },
                PROVISIONING: {
                    title: "Getting Things Ready",
                    desc: "Preparing everything needed to create your app.",
                },
                DOWNLOAD_SOURCE: {
                    title: "Collecting Your Files",
                    desc: "Gathering your app files and settings.",
                },
                INSTALL: {
                    title: "Preparing the Setup",
                    desc: "Putting all parts in place to run the build.",
                },
                PRE_BUILD: {
                    title: "Pre-Checks",
                    desc: "Making sure everything is ready before we build.",
                },
                BUILD: {
                    title: "Building Your App",
                    desc: "Creating and packaging your app.",
                },
                POST_BUILD: {
                    title: "Final Touches",
                    desc: "Adding final adjustments and optimizations.",
                },
                UPLOAD_ARTIFACTS: {
                    title: "Saving Your App",
                    desc: "Storing your completed app securely.",
                },
                FINALIZING: {
                    title: "Cleaning Up",
                    desc: "Clearing temporary files and wrapping up.",
                },
                COMPLETED: {
                    title: "Build Completed",
                    desc: "Your app is ready to download or go live.",
                },
            },
            phaseDetailsFailed: {
                SUBMITTED: {
                    title: "Build Request Failed to Submit",
                    desc: "Something went wrong while starting your build request.",
                },
                QUEUED: {
                    title: "Build Could Not Start",
                    desc: "Your build did not leave the queue due to an error.",
                },
                PROVISIONING: {
                    title: "Provisioning Failed",
                    desc: "We couldn’t prepare the environment needed for your app.",
                },
                DOWNLOAD_SOURCE: {
                    title: "File Collection Failed",
                    desc: "Your app files couldn’t be retrieved.",
                },
                INSTALL: {
                    title: "Setup Failed",
                    desc: "The setup process encountered an error.",
                },
                PRE_BUILD: {
                    title: "Pre-Build Checks Failed",
                    desc: "Some required checks failed before building.",
                },
                BUILD: {
                    title: "Build Failed",
                    desc: "We couldn’t compile or package your app.",
                },
                POST_BUILD: {
                    title: "Post-Build Steps Failed",
                    desc: "Final adjustments couldn’t be applied.",
                },
                UPLOAD_ARTIFACTS: {
                    title: "Saving Failed",
                    desc: "We couldn’t store your app’s build files.",
                },
                FINALIZING: {
                    title: "Cleanup Failed",
                    desc: "We couldn’t finish cleanup tasks.",
                },
                COMPLETED: {
                    title: "Build Ended with Errors",
                    desc: "The build process stopped due to an error.",
                },
            },
        };
    },
    computed: {
        displayPhases() {
            // If no phases, show default
            if (!this.phases || this.phases.length === 0) {
                return [
                    {
                        phaseType: "SUBMITTED",
                        status: "SUCCEEDED",
                    },
                    {
                        phaseType: "QUEUED",
                    },
                ];
            }
            // Only show phases up to and including the first FAILED phase
            const failedIdx = this.phases.findIndex(
                (p) => p.status === "FAILED" || p.status === "CLIENT_ERROR",
            );
            if (failedIdx !== -1) {
                return this.phases.slice(0, failedIdx + 1);
            }
            return this.phases;
        },
    },
    watch: {
        phases: {
            handler(newPhases) {
                // Map phaseType to phase object for quick lookup
                const phaseMap = {};
                let status = null;
                let failedFound = false;
                for (let i = 0; i < newPhases.length; i++) {
                    const phase = newPhases[i];
                    phaseMap[phase.phaseType] = phase;
                    if (
                        (phase.status === "FAILED" ||
                            phase.status === "CLIENT_ERROR") &&
                        !failedFound
                    ) {
                        status = "FAILED";
                        failedFound = true;
                    } else if (
                        phase.phaseType === "COMPLETED" &&
                        !failedFound
                    ) {
                        status = phase.phaseType;
                    }
                }
                // Count completed static steps, but stop at failed
                let completedCount = 0;
                for (let i = 0; i < this.staticPhaseTypes.length; i++) {
                    const type = this.staticPhaseTypes[i];
                    const phase = phaseMap[type];
                    if (phase) {
                        if (
                            (phase.status && phase.status === "SUCCEEDED") ||
                            type === "COMPLETED" ||
                            phase.status === "FAILED" ||
                            phase.status === "CLIENT_ERROR"
                        ) {
                            completedCount++;
                            if (
                                phase.status === "FAILED" ||
                                phase.status === "CLIENT_ERROR"
                            )
                                break;
                        } else {
                            break;
                        }
                    } else {
                        break;
                    }
                }
                const totalCount = this.staticPhaseTypes.length;
                this.progress =
                    totalCount > 0
                        ? Math.round((completedCount / totalCount) * 100)
                        : 0;
                this.buildStatus = status;
                this.$emit("update-build-status", status);
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        getPhaseStatusClass(phase) {
            if (phase.phaseType === "COMPLETED") return "complete";
            if (phase.status === "SUCCEEDED") return "complete";
            if (!phase.status) return "active";
            if (phase.status === "FAILED") return "failed";
            if (phase.status === "CLIENT_ERROR") return "failed";
            return "";
        },
        getPhaseTitle(phase) {
            const isFailed =
                phase.status === "FAILED" || phase.status === "CLIENT_ERROR";
            if (isFailed) {
                const pd = this.phaseDetailsFailed[phase.phaseType];
                return (pd && pd.title) || phase.phaseType;
            }
            const p = this.phaseDetails[phase.phaseType];
            return (p && p.title) || phase.phaseType;
        },
        getPhaseDesc(phase) {
            const isFailed =
                phase.status === "FAILED" || phase.status === "CLIENT_ERROR";
            if (isFailed) {
                const pd = this.phaseDetailsFailed[phase.phaseType];
                return (pd && pd.desc) || phase.status;
            }
            const p = this.phaseDetails[phase.phaseType];
            return (p && p.desc) || phase.status;
        },
    },
};
</script>

<style scoped>
.finalize-build-container {
    border: 1px solid #e3eaef;
    border-radius: 8px;
    padding: 16px;
    background: #fff;
    width: 100%;
}

.finalize-build-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.07);
    padding: 11px 38px 25px 38px;
    max-width: 480px;
    width: 100%;
    margin: 0 auto;
}

.finalize-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: #222;
    margin-bottom: 20px;
    text-align: left;
}

.finalize-progress-row {
    margin-bottom: 28px;
}

.finalize-progress-label {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 8px;
}

.finalize-progress-bar {
    position: relative;
    height: 8px;
    background: #e3eaef;
    border-radius: 6px;
    width: 100%;
    margin-bottom: 8px;
}

.finalize-progress-bar-inner {
    height: 8px;
    background: #2a8750;
    border-radius: 6px;
    transition: width 0.4s;
}

.finalize-progress-percent {
    position: absolute;
    right: 0;
    top: -22px;
    font-size: 13px;
    color: #222;
    font-weight: 500;
}

.finalize-steps-list {
    margin-top: 12px;
}

.finalize-step-row {
    display: flex;
    align-items: flex-start;
}

.finalize-step-icon-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-right: 18px;
    position: relative;
}

.finalize-step-icon {
    font-size: 18px;
    color: #bdbdbd;
    background: #fff;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.finalize-step-icon.complete {
    color: #2a8750;
}

.finalize-step-icon.active {
    color: #2a8750;
}

.finalize-step-icon.failed {
    color: #e53935;
}

.failed-title {
    color: #e53935 !important;
}

.failed-desc {
    color: #e53935 !important;
}

.failed-error-msg {
    color: #e53935;
    font-size: 13px;
    margin-top: 2px;
}

.failed-connector {
    background: #e53935 !important;
}

.finalize-step-connector {
    width: 2px;
    height: 32px;
    background: #e3eaef;
    margin: 0 auto;
}

.finalize-step-content {
    flex: 1;
}

.finalize-step-title {
    font-size: 1rem;
    font-weight: 600;
    color: #222;
    margin-bottom: 2px;
}

.finalize-step-desc {
    font-size: 13px;
    color: #888;
}

@media (max-width: 600px) {
    .finalize-build-card {
        padding: 18px 8px 18px 8px;
        max-width: 100%;
    }
}
</style>
